// Drag a note value (or a rest) from the palette onto the score. Pointer
// events rather than HTML5 drag and drop, so it also works with touch and the
// score can preview where the note lands while the pointer moves.

import type { DurBase } from '@simplenotation/theory';

export interface NoteDragPayload {
  base?: DurBase;
  rest?: boolean;
}

export interface DropTarget {
  /** Pointer at client coordinates: preview the drop; true if releasing here places something. */
  over(clientX: number, clientY: number, payload: NoteDragPayload): boolean;
  drop(clientX: number, clientY: number, payload: NoteDragPayload): void;
  /** The drag left the target or was cancelled: clear the preview. */
  leave(): void;
}

let target: DropTarget | null = null;

/** The score view registers itself; returns the unregister function. */
export function registerDropTarget(t: DropTarget): () => void {
  target = t;
  return () => {
    if (target === t) target = null;
  };
}

let suppressClick: { el: HTMLElement; until: number } | null = null;

/** True right after a drag ended on its own button: that click must not also run the command. */
export function consumeDragClick(el: HTMLElement): boolean {
  const s = !!suppressClick && suppressClick.el === el && performance.now() < suppressClick.until;
  suppressClick = null;
  return s;
}

const THRESHOLD = 5;

/** pointerdown on a palette button: becomes a drag once the pointer moves a few pixels. */
export function beginNoteDrag(e: React.PointerEvent<HTMLElement>, payload: NoteDragPayload): void {
  if (e.button !== 0) return;
  const source = e.currentTarget;
  const id = e.pointerId;
  const sx = e.clientX;
  const sy = e.clientY;
  let ghost: HTMLElement | null = null;
  let canDrop = false;

  const cleanup = () => {
    window.removeEventListener('pointermove', move);
    window.removeEventListener('pointerup', up);
    window.removeEventListener('pointercancel', cancel);
    window.removeEventListener('keydown', key, true);
    if (!ghost) return;
    ghost.remove();
    document.body.classList.remove('note-dragging');
    suppressClick = { el: source, until: performance.now() + 400 };
    try {
      source.releasePointerCapture(id);
    } catch {
      /* not captured */
    }
  };
  const move = (ev: PointerEvent) => {
    if (ev.pointerId !== id) return;
    if (!ghost) {
      if (Math.hypot(ev.clientX - sx, ev.clientY - sy) < THRESHOLD) return;
      ghost = document.createElement('div');
      ghost.className = 'note-drag-ghost';
      ghost.setAttribute('aria-hidden', 'true');
      ghost.innerHTML = source.innerHTML;
      document.body.appendChild(ghost);
      document.body.classList.add('note-dragging');
      try {
        source.setPointerCapture(id); // keep getting events if the pointer leaves the window
      } catch {
        /* pointer already gone */
      }
    }
    ghost.style.transform = `translate(${ev.clientX}px, ${ev.clientY}px)`;
    canDrop = target?.over(ev.clientX, ev.clientY, payload) ?? false;
    ghost.classList.toggle('can-drop', canDrop);
  };
  const up = (ev: PointerEvent) => {
    if (ev.pointerId !== id) return;
    const dragged = !!ghost;
    cleanup();
    if (!dragged) return;
    if (canDrop) target?.drop(ev.clientX, ev.clientY, payload);
    else target?.leave();
  };
  const cancel = (ev: PointerEvent) => {
    if (ev.pointerId !== id) return;
    const dragged = !!ghost;
    cleanup();
    if (dragged) target?.leave();
  };
  const key = (ev: KeyboardEvent) => {
    if (ev.key !== 'Escape' || !ghost) return;
    ev.preventDefault();
    ev.stopPropagation();
    cleanup();
    target?.leave();
  };
  window.addEventListener('pointermove', move);
  window.addEventListener('pointerup', up);
  window.addEventListener('pointercancel', cancel);
  window.addEventListener('keydown', key, true);
}
